import { useForm, Controller, set } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import {
    Box,
    Tab,
    Tabs,
    Paper,
    useTheme,
    useMediaQuery,
    Icon,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLoader } from "../../shared/contexts/LoaderContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { editarEmpresaSchema, EditarEmpresaFormData } from "./schema/editar-empresa.schema";
import { useMutation } from "@tanstack/react-query";
import { toastError } from "../../shared/services/errors/erros-handler";
import { EmpresaService } from "./service/empresa.service";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { TabDadosDaEmpresa } from "./components/tab-dados-da-empresa";
import { TabConfiguracaoEmpresa } from "./components/tab-configuracao";
import { TabEncargosCLT } from "./components/tab-encargos-CLT";
import { TabOrcamentoEmpresa } from "./components/tab-orcamento";

export function DetalheEmpresa() {
    const { showLoader, hideLoader } = useLoader();
    const navigate = useNavigate();
    const [tab, setTab] = useState(0);
    const cancellationPeriod = localStorage.getItem("cancelFinancialMovement");
    const [cancellationPeriodState, setCancellationPeriodState] = useState(cancellationPeriod);

    const methods = useForm<EditarEmpresaFormData>({
        resolver: yupResolver(editarEmpresaSchema),
        defaultValues: {
            chargesClt: undefined,
        },
    });

    const {
        handleSubmit,
        control,
        setValue,
        reset,
        formState: { errors },
    } = methods;

    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));

    const getValuesForCancellation = () => {
        return methods.getValues('companyConfig.cancelFinancialMovement');
    };

    useEffect(() => {
        setCancellationPeriodState(getValuesForCancellation());
    }, [cancellationPeriod, getValuesForCancellation()]);

    localStorage.setItem("cancelFinancialMovement", cancellationPeriodState || "false");

    const [empresaRaw, setEmpresaRaw] = useState<any>(null);
    useEffect(() => {
        showLoader();
        EmpresaService.detalhar()
            .then((empresa) => {
                setEmpresaRaw(empresa);
                reset({
                    ...empresa,
                    isClt: empresa.chargesClt !== null,
                    address: {
                        zipCode: empresa.zipCode,
                        street: empresa.street,
                        number: empresa.number,
                        complement: empresa.complement || "",
                        neighborhood: empresa.neighborhood,
                        city: empresa.city,
                        state: empresa.state,
                    },
                    profitMarginBudget: Array.isArray(empresa.profitMarginBudget) ? empresa.profitMarginBudget : [],
                });
            })
            .catch((error) => {
                toastError(error);
            })
            .finally(() => {
                hideLoader();
            });
    }, [reset]);

    const { isPending, mutate } = useMutation({

        mutationFn: async (data: EditarEmpresaFormData) => {
            const {
                fullName,
                fantasyName,
                email,
                phone,
                address,
                isClt,
                companyConfig,
                chargesClt
            } = data;
            const cleanPayload: EditarEmpresaFormData = {
                fullName,
                fantasyName,
                email,
                phone,
                address,
                isClt,
                companyConfig,
                profitMarginBudget: data.profitMarginBudget || [],
                laborBudget: data.laborBudget || [],
                ...(isClt ? { chargesClt } : {}),
            };
            const result = await EmpresaService.editar(cleanPayload);
            return result;
        },
        onSuccess: (_, variables) => {
            if (variables?.companyConfig?.alicota !== undefined) {
                localStorage.setItem(
                    "alicota",
                    Number(variables.companyConfig.alicota).toString()
                );
            }
            if (variables?.companyConfig?.cancelFinancialMovement !== undefined) {
                localStorage.setItem(
                    "cancelFinancialMovement",
                    JSON.stringify(!!variables.companyConfig.cancelFinancialMovement)
                );
            }

            if (variables?.companyConfig?.cancellationPeriod !== undefined) {
                localStorage.setItem(
                    "cancellationPeriod",
                    Number(variables.companyConfig.cancellationPeriod).toString()
                );
            }

            if (variables?.chargesClt !== undefined) {
                const objetoChargesClt = variables.chargesClt;
                const somaEncargosClt = objetoChargesClt.fgtsPercent +
                    objetoChargesClt.fgtsTerminationFine +
                    objetoChargesClt.inssEmployerPercent +
                    objetoChargesClt.ratPercent +
                    objetoChargesClt.vacationPercent +
                    objetoChargesClt.thirteenthSalaryPercent +
                    objetoChargesClt.oneThirdVacationPercent +
                    objetoChargesClt.fgtsOnProvisionsPercent;
                localStorage.setItem(
                    "chargesClt",
                    Number(somaEncargosClt).toString()
                );
            }
            toast.success("Dados atualizados com sucesso!");
        },
        onError: (err: any) => {
            toastError(err);
        },
        onMutate: () => {
            showLoader();
        },
        onSettled: () => {
            hideLoader();
        },
    });

    function mascaraValor(value: string): string {
        throw new Error("Function not implemented.");
    }

    return (
        <BaseLayout title="Detalhe da Empresa">
            <FerramanetasDetalhe
                mostraBotaoSalvarEVoltar={false}
                mostraBotaoApagar={false}
                mostraBotaoNovo={false}
                mostraBotaoVoltar={false}
                aoClicarBotaoSalvar={handleSubmit((formData) => {

                    mutate(formData);
                })}
            />
            <Paper sx={{ p: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab label="Dados da Empresa" />
                    <Tab label="Configuração" />
                    {methods.watch("isClt") && <Tab label="Parametrização Encargos CLT" />}
                    <Tab label={<Box display="flex" alignItems="center" gap={2}><Icon>settings</Icon> Configuração Orçamento</Box>} />
                </Tabs>
                {/* Aba 0: Dados da Empresa */}
                {tab === 0 && empresaRaw && (
                    <TabDadosDaEmpresa
                        empresaRaw={empresaRaw}
                        control={control}
                        errors={errors}
                        methods={methods}
                        setTab={setTab}
                        mdDown={mdDown}
                    />
                )}
                {/* Aba 1: Configuração */}
                {tab === 1 && (
                    <TabConfiguracaoEmpresa
                        empresaRaw={empresaRaw}
                        control={control}
                        errors={errors}
                        methods={methods}
                        setTab={setTab}
                        mdDown={mdDown}
                    />
                )}
                {/* Aba 2: Encargos CLT */}
                {tab === 2 && (
                    <TabEncargosCLT
                        empresaRaw={empresaRaw}
                        control={control}
                        errors={errors}
                        methods={methods}
                        setTab={setTab}
                        mdDown={mdDown}
                    />
                )}
                {/* Aba 3: Orçamento de Mão de Obra */}
                {tab === 3 && (
                    <TabOrcamentoEmpresa
                        empresaRaw={empresaRaw}
                        control={control}
                        errors={errors}
                        methods={methods}
                        setTab={setTab}
                        mdDown={mdDown}
                    />
                )}
            </Paper>
        </BaseLayout>
    );
}
